/**
 * Universal capability scoring for a single Reference Store run.
 *
 * Each scorer answers ONE question — the same question, no matter what
 * kind of retailer the fixture is. Store type is context, never a template.
 *
 * Some checks are heuristic today and will be replaced by an LLM judge
 * once the loop is stable — those are marked with LLM_JUDGE.
 */

import type { EngineOutput } from "../types";
import type { ReferenceStoreFixture } from "./fixtures";
import type { ReasoningCapability, ReferenceStore } from "../reference-stores/types";

export interface CapabilityScore {
  capability: ReasoningCapability;
  passed: boolean;
  note: string;
}

export interface StoreScorecard {
  slug: string;
  passed: number;
  total: number;
  scores: CapabilityScore[];
  surprise_candidates: string[];   // opportunities that go beyond obvious gap-closing
}

const SEO_JARGON = [
  "keyword", "keywords", "serp", "ranking", "ranks", "meta tag",
  "backlink", "anchor text", "crawl", "index", "seo",
];

function anyWordOverlap(a: string, b: string): boolean {
  const words = new Set(
    a.toLowerCase().split(/\W+/).filter((w) => w.length > 3),
  );
  return b.toLowerCase().split(/\W+/).some((w) => w.length > 3 && words.has(w));
}

export function scoreRun(
  fixture: ReferenceStoreFixture,
  refStore: Pick<ReferenceStore, "slug" | "context" | "disallowed">,
  output: EngineOutput,
): StoreScorecard {
  const scores: CapabilityScore[] = [];
  const ops = output.opportunities;
  const top = ops.slice(0, 3);
  const ctx = fixture.business_context;

  // 1. identifies_demand
  scores.push({
    capability: "identifies_demand",
    passed:
      ops.length > 0 &&
      ops.every((o) => o.evidence.some((e) => e.evidence_type === "demand")),
    note:
      ops.length === 0
        ? "No opportunities produced."
        : "Every opportunity carries a demand evidence item.",
  });

  // 2. compares_to_visibility
  scores.push({
    capability: "compares_to_visibility",
    passed:
      ops.length > 0 &&
      ops.every((o) => o.evidence.some((e) => e.evidence_type === "visibility")),
    note: "Each opportunity pairs demand with a visibility observation.",
  });

  // 3. respects_strengths — LLM_JUDGE eventually. Heuristic: at least one
  //    top opportunity mentions words that also appear in a stated strength.
  const strengthText = ctx.strengths.join(" ");
  scores.push({
    capability: "respects_strengths",
    passed: top.some((o) =>
      anyWordOverlap(strengthText, `${o.title} ${o.what_we_found} ${o.why_it_matters}`),
    ),
    note: "At least one top opportunity connects to a stated strength.",
  });

  // 4. respects_goals — LLM_JUDGE eventually. Heuristic: goals surface in
  //    opportunity language or estimated_business_outcome.
  const goalText = ctx.goals.join(" ");
  scores.push({
    capability: "respects_goals",
    passed: top.some((o) =>
      anyWordOverlap(
        goalText,
        `${o.title} ${o.why_it_matters} ${o.recommended_next_move} ${o.estimated_business_outcome ?? ""}`,
      ),
    ),
    note: "Top opportunities move toward a stated goal.",
  });

  // 5. respects_constraints — top opportunities don't demand capacity the store lacks.
  //    Heuristic proxy: no top opp reads as a heavy new service if 'capacity' is constrained.
  const constraintText = ctx.constraints.join(" ").toLowerCase();
  const capacityConstrained = /capacity|small team|no.*hire|limited time/.test(constraintText);
  const heavyLift = top.some((o) => o.effort === "high");
  scores.push({
    capability: "respects_constraints",
    passed: !(capacityConstrained && heavyLift),
    note: capacityConstrained
      ? heavyLift
        ? "Top opportunities include a high-effort ask despite capacity constraints."
        : "No top opportunity requires effort the retailer said they can't spare."
      : "No hard capacity constraint declared.",
  });

  // 6. avoids_disallowed — none of the surfaced opps should match a
  //    disallowed action description.
  const disallowedDescriptions = (refStore.disallowed ?? []).map((d) =>
    d.description.toLowerCase(),
  );
  const violated = ops.filter((o) =>
    disallowedDescriptions.some((desc) =>
      anyWordOverlap(desc, `${o.title} ${o.recommended_next_move}`),
    ),
  );
  scores.push({
    capability: "avoids_disallowed",
    passed: violated.length === 0,
    note:
      violated.length === 0
        ? "No disallowed action was recommended."
        : `Violated: ${violated.map((v) => v.title).join(", ")}`,
  });

  // 7. prioritizes_usefully — top-ranked opp should have impact >= medium
  //    and confidence >= medium (biggest lever, not a minor tweak).
  const first = ops[0];
  scores.push({
    capability: "prioritizes_usefully",
    passed:
      !!first &&
      first.impact !== "low" &&
      first.confidence !== "low" &&
      (first.horizon === "this_week" || first.horizon === "this_season"),
    note: first
      ? `#1 = ${first.title} (impact=${first.impact}, confidence=${first.confidence}, horizon=${first.horizon}).`
      : "No #1 opportunity.",
  });

  // 8. explains_plainly — no SEO jargon anywhere in retailer-facing fields.
  const jargonHits: string[] = [];
  for (const o of ops) {
    const text = `${o.title} ${o.what_we_found} ${o.why_it_matters} ${o.recommended_next_move}`.toLowerCase();
    for (const j of SEO_JARGON) {
      if (new RegExp(`\\b${j}\\b`).test(text)) jargonHits.push(`${o.title}: "${j}"`);
    }
  }
  scores.push({
    capability: "explains_plainly",
    passed: jargonHits.length === 0,
    note:
      jargonHits.length === 0
        ? "No SEO jargon in retailer-facing copy."
        : `Jargon leaked: ${jargonHits.join("; ")}`,
  });

  // Surprise: any opportunity that goes beyond raw gap-closing — currently
  //    approximated as opportunities whose evidence includes a rising trend
  //    the engine chose to lean into. Placeholder metric until an LLM judge
  //    can rate genuine novelty.
  const surprise_candidates = ops
    .filter((o) =>
      o.evidence.some((e) => (e.detail as { trend?: string })?.trend === "rising"),
    )
    .map((o) => o.title);

  return {
    slug: fixture.slug,
    passed: scores.filter((s) => s.passed).length,
    total: scores.length,
    scores,
    surprise_candidates,
  };
}